// src/context/CartContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '@/api/client'
import { useAuth } from './AuthContext'

const CartContext = createContext(null)

export function CartProvider({ children }) {
  const { user } = useAuth()
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(false)

  const fetchCart = useCallback(async () => {
    if (!user) { setItems([]); return }
    try {
      const { data } = await api.get('/buyer/cart')
      setItems(data.data || [])
    } catch { setItems([]) }
  }, [user])

  useEffect(() => { fetchCart() }, [fetchCart])

  const addItem = useCallback(async (productId, quantity = 1, variantId = null) => {
    setLoading(true)
    const { data } = await api.post('/buyer/cart', { product_id: productId, quantity, variant_id: variantId })
    setItems(data.data)
    setLoading(false)
    return data
  }, [])

  const updateItem = useCallback(async (itemId, quantity) => {
    const { data } = await api.patch(`/buyer/cart/${itemId}`, { quantity })
    setItems(data.data)
  }, [])

  const removeItem = useCallback(async (itemId) => {
    const { data } = await api.delete(`/buyer/cart/${itemId}`)
    setItems(data.data)
  }, [])

  const clearCart = useCallback(async () => {
    await api.delete('/buyer/cart')
    setItems([])
  }, [])

  const count    = items.reduce((s, i) => s + i.quantity, 0)
  const subtotal = items.reduce((s, i) => s + i.quantity * parseFloat(i.price), 0)

  return (
    <CartContext.Provider value={{ items, count, subtotal, loading, addItem, updateItem, removeItem, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => useContext(CartContext)
